import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Icon } from "./Icon";
import { useI18n, type Translate } from "../lib/i18n";

/**
 * A short, skippable walk round the app, pointing at the real thing.
 *
 * ── Why a tour at all ───────────────────────────────────────────────────────
 * The production numbers this app was rebuilt against were 282 activities
 * planned and 9 ever ticked. Families were not refusing to teach; they did
 * not find the tick, the guide, or the record button. A parent who has just
 * named their child is the most willing reader the app will ever have, so
 * this is the moment to show where those live — once, quickly, and only if
 * they want it.
 *
 * ── How it points ───────────────────────────────────────────────────────────
 * Each step names an element by its `data-tour` attribute, and a spotlight is
 * drawn over that element's live position — the actual button, not a
 * screenshot of it — re-measured on scroll, resize and while a drawer slides.
 * A step whose element is not on screen falls back to a centred card rather
 * than pointing at nothing. On a phone the navigation is a drawer, so steps
 * marked `inNav` ask the app to open it first.
 *
 * Skippable from every step, Escape included, and replayable from the menu.
 */

export type TourStep = {
  id: string;
  /** `data-tour` value of the element to point at. None: a centred card. */
  target?: string;
  title: string;
  body: string;
  /** Lives in the sidebar, which on a phone is a drawer that must be opened. */
  inNav?: boolean;
  /** Where the card prefers to sit. Sidebar items read best beside, the top bar below. */
  placement?: "side" | "below";
};

/**
 * The steps for a family, with their children's names in them.
 *
 * Memoise the result: each step's identity is what the tour re-measures on,
 * so a fresh array on every render would re-scroll to the target every render.
 */
export function familyTourSteps(t: Translate, names: { guardian: string; child: string }): TourStep[] {
  const child = names.child || t("tour.yourChild");
  return [
    {
      id: "welcome",
      title: names.guardian ? t("tour.welcome.title", { name: names.guardian }) : t("tour.welcome.titlePlain"),
      body: t("tour.welcome.body"),
    },
    { id: "learner", target: "learner", placement: "below", title: t("tour.learner.title", { child }), body: t("tour.learner.body", { child }) },
    // Titled with the very labels on the buttons they point at, so the tour
    // can never call a page something the menu does not.
    { id: "home", target: "nav-home", inNav: true, placement: "side", title: t("nav.home"), body: t("tour.home.body") },
    { id: "today", target: "nav-today", inNav: true, placement: "side", title: t("nav.today"), body: t("tour.today.body") },
    { id: "record", target: "record", placement: "below", title: t("top.recordEvidence"), body: t("tour.record.body", { child }) },
    { id: "curriculum", target: "nav-curriculum", inNav: true, placement: "side", title: t("nav.curriculum"), body: t("tour.curriculum.body") },
    { id: "mastery", target: "nav-mastery", inNav: true, placement: "side", title: t("nav.mastery"), body: t("tour.mastery.body", { child }) },
    { id: "portfolio", target: "nav-portfolio", inNav: true, placement: "side", title: t("nav.portfolio"), body: t("tour.portfolio.body") },
    { id: "language", target: "language", placement: "below", title: t("tour.language.title"), body: t("tour.language.body") },
    { id: "done", title: t("tour.done.title"), body: t("tour.done.body", { child }) },
  ];
}

type Box = { top: number; left: number; width: number; height: number };

const PAD = 6;
const GAP = 14;
const EDGE = 12;

function sameBox(a: Box | null, b: Box | null) {
  return a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);
}

function boxOf(target: string | undefined): Box | null {
  if (!target) return null;
  const element = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  const onScreen =
    rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
    rect.top < window.innerHeight && rect.left < window.innerWidth;
  return onScreen
    ? { top: Math.round(rect.top), left: Math.round(rect.left), width: Math.round(rect.width), height: Math.round(rect.height) }
    : null;
}

/** Where the card goes: beside or below the spotlight, flipped and clamped to stay on screen. */
function cardPosition(box: Box | null, card: { width: number; height: number }, step: TourStep, rtl: boolean): CSSProperties {
  // Centred by inset and auto margins, not a transform: the entrance
  // animation owns `transform`, and would knock a transform-centred card off
  // centre for as long as it ran.
  if (!box) return { inset: 0, margin: "auto", height: "fit-content" };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(card.width || 340, vw - EDGE * 2);
  const height = card.height || 220;
  const clampX = (x: number) => Math.min(Math.max(x, EDGE), vw - width - EDGE);
  const clampY = (y: number) => Math.min(Math.max(y, EDGE), vh - height - EDGE);

  if (step.placement === "side") {
    const room = rtl ? box.left - PAD : vw - (box.left + box.width + PAD);
    if (room >= width + GAP + EDGE) {
      const left = rtl ? box.left - PAD - GAP - width : box.left + box.width + PAD + GAP;
      return { top: clampY(box.top + box.height / 2 - height / 2), left };
    }
  }
  const centred = clampX(box.left + box.width / 2 - width / 2);
  if (vh - (box.top + box.height + PAD) >= height + GAP + EDGE) {
    return { top: box.top + box.height + PAD + GAP, left: centred };
  }
  if (box.top - PAD >= height + GAP + EDGE) {
    return { top: box.top - PAD - GAP - height, left: centred };
  }
  return { top: vh - height - EDGE, left: centred };
}

export function Tour({
  steps,
  onClose,
  onStep,
}: {
  steps: TourStep[];
  /** `finished` is true when they reached the end, false when they skipped. */
  onClose: (finished: boolean) => void;
  /** Called as each step opens, so the app can open or close the phone drawer. */
  onStep?: (step: TourStep) => void;
}) {
  const { t, dir } = useI18n();
  const [index, setIndex] = useState(0);
  const step = steps[Math.min(index, steps.length - 1)];
  const [box, setBox] = useState<Box | null>(null);
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
  const card = useRef<HTMLDivElement>(null);
  const primary = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<Element | null>(null);
  const last = index === steps.length - 1;
  const rtl = dir === "rtl";

  const finish = useCallback((finished: boolean) => onClose(finished), [onClose]);
  const next = useCallback(() => (last ? finish(true) : setIndex((value) => value + 1)), [last, finish]);
  const back = useCallback(() => setIndex((value) => Math.max(0, value - 1)), []);

  // Remember where focus was, and give it back when the tour ends.
  useEffect(() => {
    returnFocus.current = document.activeElement;
    return () => {
      if (returnFocus.current instanceof HTMLElement) returnFocus.current.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    onStep?.(step);
  }, [step, onStep]);

  // Follow the target. Measured for a moment after each step opens — a phone
  // drawer slides for a quarter of a second — then again on scroll and resize.
  useLayoutEffect(() => {
    const element = step.target ? document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`) : null;
    element?.scrollIntoView({ block: "nearest", inline: "nearest" });
    let frame = 0;
    let until = performance.now() + 650;
    const measure = () => {
      const next = boxOf(step.target);
      setBox((current) => (sameBox(current, next) ? current : next));
      if (performance.now() < until) frame = requestAnimationFrame(measure);
    };
    measure();
    const follow = () => {
      cancelAnimationFrame(frame);
      until = performance.now() + 120;
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow, true);
    };
  }, [step]);

  // The card's own size decides whether it fits beside or below. Re-attached
  // when the card switches between centred and anchored, which remounts it.
  const anchored = box !== null;
  useLayoutEffect(() => {
    const node = card.current;
    if (!node) return;
    const read = () => {
      const { width, height } = node.getBoundingClientRect();
      setCardSize((current) =>
        Math.round(current.width) === Math.round(width) && Math.round(current.height) === Math.round(height)
          ? current
          : { width, height },
      );
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [anchored]);

  useEffect(() => {
    primary.current?.focus({ preventScroll: true });
  }, [index]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(false);
      } else if (event.key === (rtl ? "ArrowLeft" : "ArrowRight")) {
        next();
      } else if (event.key === (rtl ? "ArrowRight" : "ArrowLeft")) {
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finish, next, back, rtl]);

  const titleId = `tour-title-${step.id}`;
  const bodyId = `tour-body-${step.id}`;

  return (
    <div className={box ? "tour-layer" : "tour-layer dim"} role="presentation">
      {box && (
        <div
          className="tour-spot"
          aria-hidden="true"
          style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + PAD * 2, height: box.height + PAD * 2 }}
        />
      )}
      <div
        ref={card}
        // A new element when the card changes between centred and anchored:
        // its position would otherwise slide in from the corner of the screen.
        key={anchored ? "anchored" : "centred"}
        className={box ? "tour-card" : "tour-card centred"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        style={cardPosition(box, cardSize, step, rtl)}
      >
        <div className="tour-card-head">
          <span className="tour-count">{t("tour.count", { n: index + 1, total: steps.length })}</span>
          <button type="button" className="tour-skip" onClick={() => finish(false)}>
            {last ? t("common.close") : t("tour.skip")}
          </button>
        </div>
        {/* The key restarts the entrance animation on every step. */}
        <div className="tour-copy" key={step.id} aria-live="polite">
          <h2 id={titleId}>{step.title}</h2>
          <p id={bodyId}>{step.body}</p>
        </div>
        <div className="tour-dots" aria-hidden="true">
          {steps.map((entry, position) => (
            <i key={entry.id} className={position === index ? "on" : position < index ? "seen" : ""} />
          ))}
        </div>
        <div className="tour-actions">
          {index > 0 && !last ? (
            <button type="button" className="soft-button" onClick={back}>
              {t("tour.back")}
            </button>
          ) : (
            <span />
          )}
          <button ref={primary} type="button" className="primary" onClick={next}>
            {index === 0 ? t("tour.start") : last ? t("tour.finish") : t("tour.next")}
            {!last && <Icon name="arrow" size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}

const TOUR_KEY = (userId: string) => `homeschool-tour:${userId}`;

/** Whether this person has already been shown round, on this device. */
export function tourSeen(userId: string) {
  try {
    return localStorage.getItem(TOUR_KEY(userId)) !== null;
  } catch {
    return true; // Storage refused: never nag on every visit.
  }
}

export function rememberTour(userId: string, finished: boolean) {
  try {
    localStorage.setItem(TOUR_KEY(userId), finished ? "finished" : "skipped");
  } catch {
    /* Storage is optional. */
  }
}
